import { useState } from "react";

// Número de WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = "5547996077227";

// Opções da área do caso — editáveis diretamente aqui no HTML
const AREAS_DO_CASO = [
  "Direito Médico",
  "Direito à Saúde do Paciente",
  "Direito Previdenciário",
  "Direito de Família e Sucessão",
  "Direito Trabalhista",
  "LGPD",
  "Direito Civil",
  "Direito Penal",
  "Direito Administrativo",
];

export default function Contato() {
  const [charCount, setCharCount] = useState(0);
  const [sent, setSent] = useState(false);

  /**
   * Ao submeter o formulário:
   * 1. Previne o comportamento padrão (sem reload, sem fetch)
   * 2. Captura os valores dos campos
   * 3. Monta a mensagem no formato exato solicitado
   * 4. Codifica com encodeURIComponent
   * 5. Abre o WhatsApp em nova aba com a mensagem pronta
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;

    // Captura os valores dos campos
    const nome = (form.elements.namedItem("nome") as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem("email") as HTMLInputElement).value.trim();
    const area = (form.elements.namedItem("area") as HTMLSelectElement).value;
    const mensagem = (form.elements.namedItem("mensagem") as HTMLTextAreaElement).value.trim();

    // Valida limite de caracteres da mensagem
    if (mensagem.length > 500) {
      return;
    }

    // Monta a mensagem no formato exato solicitado
    const texto =
      `Nome Completo: ${nome} | E-mail: ${email} | Área do Caso: ${area} | Motivo do Contato: ${mensagem}`;

    // Codifica para URL e abre o WhatsApp em nova aba
    const textoCodificado = encodeURIComponent(texto);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${textoCodificado}`, "_blank");

    // Feedback visual de sucesso
    setSent(true);
    form.reset();
    setCharCount(0);
  };

  return (
    <section id="contato" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-px bg-[#C8A63A]" />
            <span
              className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C8A63A]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Fale Conosco
            </span>
            <div className="w-10 h-px bg-[#C8A63A]" />
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0A2472] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Contato
          </h2>
          <p
            className="text-gray-500 max-w-xl mx-auto text-sm"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Atendimento com ética, sigilo e excelência.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Formulário */}
          <div className="bg-[#F2F2F2] rounded-lg p-8">
            <h3
              className="text-xl font-bold text-[#0A2472] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Envie uma mensagem
            </h3>

            {sent ? (
              /* Estado de sucesso após envio */
              <div className="text-center py-12">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: "linear-gradient(135deg, #C8A63A, #E6D18A)" }}
                >
                  <i className="ri-whatsapp-line text-3xl text-[#0A2472]" />
                </div>
                <h4
                  className="font-bold text-[#0A2472] text-lg mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Redirecionando para o WhatsApp!
                </h4>
                <p
                  className="text-gray-500 text-sm"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Sua mensagem já está pronta para envio no WhatsApp.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 px-6 py-2.5 rounded-md text-sm font-semibold text-[#0A2472] cursor-pointer whitespace-nowrap"
                  style={{
                    background: "linear-gradient(135deg, #C8A63A, #E6D18A)",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              /* Formulário principal */
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-4">

                  {/* Campo: Nome Completo */}
                  <div>
                    <label
                      className="block text-xs font-semibold text-[#0A2472] mb-1.5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="nome"
                      required
                      className="w-full px-4 py-3 rounded-md border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#C8A63A] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      placeholder="Seu nome completo"
                    />
                  </div>

                  {/* Campo: E-mail */}
                  <div>
                    <label
                      className="block text-xs font-semibold text-[#0A2472] mb-1.5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-md border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#C8A63A] transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      placeholder="seu@email.com"
                    />
                  </div>

                  {/* Campo: Área do Caso — opções dinâmicas e editáveis */}
                  <div>
                    <label
                      className="block text-xs font-semibold text-[#0A2472] mb-1.5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Área do Caso *
                    </label>
                    <select
                      name="area"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 rounded-md border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#C8A63A] transition-colors cursor-pointer"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      <option value="" disabled>
                        Selecione a área do seu caso
                      </option>
                      {/* Opções dinâmicas — edite o array AREAS_DO_CASO acima para alterar */}
                      {AREAS_DO_CASO.map((area) => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Campo: Mensagem */}
                  <div>
                    <label
                      className="block text-xs font-semibold text-[#0A2472] mb-1.5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Mensagem *
                    </label>
                    <textarea
                      name="mensagem"
                      required
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#C8A63A] transition-colors resize-none"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      placeholder="Descreva brevemente sua situação..."
                    />
                    <div
                      className="text-right text-xs text-gray-400 mt-1"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {charCount}/500
                    </div>
                  </div>
                </div>

                {/* Botão de envio — abre WhatsApp com mensagem pronta */}
                <button
                  type="submit"
                  className="w-full mt-6 py-3.5 rounded-md font-semibold text-sm text-[#0A2472] transition-all duration-300 hover:opacity-90 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  style={{
                    background: "linear-gradient(135deg, #C8A63A, #E6D18A)",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <i className="ri-whatsapp-line text-base" />
                  Enviar via WhatsApp
                </button>
              </form>
            )}
          </div>

          {/* Informações de contato */}
          <div className="space-y-8">
            <div>
              <h3
                className="text-xl font-bold text-[#0A2472] mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Informações de contato
              </h3>
              <div className="space-y-5">
                {[
                  {
                    icon: "ri-map-pin-line",
                    label: "Endereço",
                    value:
                      "Av. Juscelino Kubitschek, 410 – Sala 305A\nCentro – Joinville/SC – CEP: 89201-100",
                  },
                  {
                    icon: "ri-whatsapp-line",
                    label: "WhatsApp",
                    value: "(47) 99607-7227",
                    href: `https://wa.me/${WHATSAPP_NUMBER}`,
                  },
                  {
                    icon: "ri-mail-line",
                    label: "E-mail",
                    value: "quelen.c.manske@gmail.com",
                    href: "mailto:quelen.c.manske@gmail.com",
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0"
                      style={{ background: "linear-gradient(135deg, #C8A63A20, #E6D18A30)" }}
                    >
                      <i className={`${item.icon} text-lg`} style={{ color: "#C8A63A" }} />
                    </div>
                    <div>
                      <div
                        className="text-xs font-semibold text-gray-400 mb-0.5 uppercase tracking-wide"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {item.label}
                      </div>
                      {item.href ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-[#0A2472] text-sm font-medium hover:text-[#C8A63A] transition-colors cursor-pointer"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span
                          className="text-[#0A2472] text-sm font-medium whitespace-pre-line"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.value}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Redes Sociais */}
            <div>
              <h4
                className="text-sm font-bold text-[#0A2472] mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Redes Sociais
              </h4>
              <div className="flex gap-3">
                {[
                  {
                    icon: "ri-instagram-line",
                    href: "https://www.instagram.com/qmanske",
                    label: "Instagram",
                  },
                  {
                    icon: "ri-facebook-line",
                    href: "https://www.facebook.com/quelenbeatriz.crizelmanske",
                    label: "Facebook",
                  },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-10 h-10 flex items-center justify-center rounded-md border border-[#C8A63A]/30 text-[#C8A63A] hover:border-[#C8A63A] hover:bg-[#C8A63A]/10 transition-all duration-300 cursor-pointer"
                  >
                    <i className={`${s.icon} text-lg`} />
                  </a>
                ))}
              </div>
            </div>

            {/* Mapa */}
            <div className="rounded-lg overflow-hidden w-full h-52">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3567.1234567890!2d-48.8511!3d-26.3044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94de6fbc0b26ac6d%3A0x1e6d5c1e4e4b2c8a!2sAv.%20Juscelino%20Kubitschek%2C%20410%20-%20Centro%2C%20Joinville%20-%20SC%2C%2089201-100!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Quelen Manske Advocacia"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
